/**
 * The append-only revision trail, as the browser reads it — IR-126 §3.1, 5.4.
 *
 * One collection per project, `projects/{pid}/revisions/{autoId}`, carrying
 * entries for every revisable entity and discriminated by `entity_kind` +
 * `entity_id`. Written exclusively by the apply layer; the rules give clients
 * `allow read` and `allow write: if false`.
 *
 * **Three properties of the stored shape drive everything here, and each one
 * has a way of being got wrong that is invisible in review.**
 *
 * 1. *The document id is an auto-id and carries no order.* `seq` is per-entity,
 *    so `seq 5` exists for many tasks and cannot be the key either. Ordering is
 *    always explicit and always numeric — see {@link bySeq}.
 *
 * 2. *An absent `after` means `null`, and `null` means cleared.* Revisions are
 *    stored through Pydantic's `model_dump(exclude_none=True)`, applied
 *    recursively, so a change that clears a field stores as `{field, before}`
 *    with **no `after` key at all**. `TaskRevision`'s own docstring names this
 *    reader: "a client model typing `after` as required, or rendering a missing
 *    `after` as 'unchanged', inverts the meaning of every clearing entry — the
 *    case an audit reader most needs to see." {@link parseChange} normalises
 *    absence to `null` so nothing downstream can read it as "unchanged".
 *
 * 3. *A reset is not labelled.* Both content modes of `task_reapprove` write
 *    `entry_type: 'reapproved'`, and `content_mode` never reaches the revision
 *    — so "was this a RESET?" is derived, not read. See {@link revertedFields}.
 */
import type { DocumentData, Timestamp } from 'firebase/firestore'

/** The collection, relative to a project document. */
export const REVISIONS_COLLECTION = 'revisions'

/** The only revisable kind today — `ENTITY_KIND_PROJECT_TASK` in CMS. */
export const ENTITY_KIND_PROJECT_TASK = 'project_task'

/**
 * The revision every task starts at, and the one `RESET` always returns to.
 *
 * Not a magic number: `RESET` targets `seq 0` and never "the last reset point",
 * so a task reset twice returns to the AI original both times. That fixed
 * target is why the trail does not need the reset revision to record where it
 * reverted to — the answer is always this.
 */
export const GENESIS_SEQ = 0

/**
 * What kind of change an entry records. Mirrors CMS `RevisionEntryType`.
 *
 * Kept as a union of string literals with an `unknown` member rather than a
 * closed set, for the reason CMS gives its `_missing_` fallback: a value
 * written by a newly deployed service must survive being read by a browser
 * running older code.
 */
export type RevisionEntryType =
  | 'created'
  // MYAC-1639. The genesis of a hidden task an undecided action got at its first
  // comment or edit. Not shown: in the user's sense the task is created by the
  // later acceptance, which the trail states instead (see `taskTrailEvent`).
  | 'parked'
  | 'legacy_created'
  | 'content_edit'
  | 'review_transition'
  | 'assignment'
  | 'unapproved'
  | 'reapproved'
  | 'archived'
  // MYAC-1266. Three, not one: a reader asking "what happened to this task"
  // needs to tell a conversation from a retraction.
  | 'comment_added'
  | 'comment_edited'
  | 'comment_withdrawn'
  | 'unknown'

/** Who made the change. `agent` is Admin-SDK only — rules forbid a client setting it. */
export interface RevisionActor {
  userId: string | null
  aiGenerated: boolean
  agent: string | null
}

/**
 * One field's before and after.
 *
 * Both sides are `unknown` and both may be `null`. `null` on either side means
 * the field held no value at that point — **not** that it did not change; a
 * change is recorded only because something moved.
 */
export interface RevisionChange {
  field: string
  before: unknown
  after: unknown
}

/** One entry in a project's trail. */
export interface TaskRevision {
  id: string
  entityKind: string
  entityId: string
  seq: number
  at: Date | null
  actor: RevisionActor | null
  entryType: RevisionEntryType
  changes: RevisionChange[]
  /** Full content, on `seq 0` only. Deliberately untyped — it must stay readable across schema drift. */
  snapshot: Record<string, unknown> | null
  /** `'proposal' | 'task'` on a *backfilled* genesis; absent on one written at create time. */
  snapshotSource: string | null
  originRef: string | null
  commandRef: string | null
}

function asDate(value: unknown): Date | null {
  if (value == null) return null
  if (value instanceof Date) return value
  const stamp = value as Partial<Timestamp>
  return typeof stamp.toDate === 'function' ? stamp.toDate() : null
}

/**
 * One stored change, with absence normalised to `null`.
 *
 * `?? null` rather than `'after' in raw`: absent and explicitly-null mean the
 * same thing on the wire (`exclude_none` produced the absence *from* a null),
 * so there is no third state worth preserving — and collapsing them here means
 * no caller can accidentally branch on which one it got. `??` leaves `''`, `0`
 * and `false` alone, which matters: an empty string is a value a user typed.
 */
export function parseChange(raw: DocumentData): RevisionChange {
  return {
    field: String(raw.field ?? ''),
    before: raw.before ?? null,
    after: raw.after ?? null,
  }
}

function parseActor(raw: unknown): RevisionActor | null {
  if (raw == null || typeof raw !== 'object') return null
  const actor = raw as DocumentData
  return {
    userId: actor.user_id != null ? String(actor.user_id) : null,
    aiGenerated: actor.ai_generated === true,
    agent: actor.agent != null ? String(actor.agent) : null,
  }
}

/** A stored revision document, as the trail uses it. */
export function parseRevision(id: string, raw: DocumentData): TaskRevision {
  return {
    id,
    entityKind: String(raw.entity_kind ?? ENTITY_KIND_PROJECT_TASK),
    entityId: String(raw.entity_id ?? ''),
    // `Number(...)` not `?? 0`: a missing seq is a construction bug in the
    // writer, and NaN sorts to the end loudly rather than masquerading as
    // genesis — which is the one seq that has meaning attached to it.
    seq: Number(raw.seq),
    at: asDate(raw.at),
    actor: parseActor(raw.actor),
    entryType: (raw.entry_type ?? 'unknown') as RevisionEntryType,
    changes: Array.isArray(raw.changes) ? raw.changes.map(parseChange) : [],
    snapshot: (raw.snapshot as Record<string, unknown>) ?? null,
    snapshotSource: raw.snapshot_source != null ? String(raw.snapshot_source) : null,
    originRef: raw.origin_ref != null ? String(raw.origin_ref) : null,
    commandRef: raw.command_ref != null ? String(raw.command_ref) : null,
  }
}

/**
 * Oldest first, **numerically**.
 *
 * The Done-when names this case: past nine revisions, lexicographic and numeric
 * ordering diverge, so a trail sorted as strings puts `seq 10` between `1` and
 * `2`. The Firestore `orderBy('seq')` is already numeric because `seq` is a
 * number field — this exists so any client-side re-sort cannot quietly
 * reintroduce the bug, and so a caller that merges pages still has one
 * definition of order.
 */
export function bySeq(a: TaskRevision, b: TaskRevision): number {
  return a.seq - b.seq
}

/**
 * The two fields a re-approve records because it *revived* the task, as opposed
 * to because it reverted content.
 *
 * `apply_reapprove` prepends exactly these — `review_decision` always, and
 * `review_rejection_comment` when one was set — and then appends whatever a
 * RESET reverted. Neither is in `EditPolicy.editable_fields`, so the two lists
 * cannot overlap; that is what makes the discriminator below safe.
 *
 * **Deliberately these two rather than a mirror of `editable_fields`.** Both
 * would work today, but this set is pinned by two hard-coded lines in one
 * function, while `editable_fields` is a growing registry — mirroring the
 * larger, more mobile set is the copy that drifts.
 */
const REVIVAL_FIELDS: ReadonlySet<string> = new Set([
  'review_decision',
  'review_rejection_comment',
  // MYAC-1089 — a revival also clears a rejection's period when it had one; it
  // is the rejection's, not content, so it must not make a KEEP read as a RESET.
  'review_rejected_until',
  // MYAC-1639 — the rejection's reason, cleared by a revival like its comment;
  // and the `parked → active` move an acceptance records. Neither is content.
  'review_rejection_reason',
  'lifecycle',
])

/**
 * Which fields this entry reverted — empty unless it is a RESET.
 *
 * The card's premise, stated exactly: *"the reset revision already names every
 * field it reverted"*. It does, in `changes` — `before` is the edited value and
 * `after` is the genesis value. What it does **not** name is the seq it
 * reverted to, and it does not need to: `RESET` always targets {@link
 * GENESIS_SEQ}.
 *
 * So a `reapproved` entry carrying any change outside {@link REVIVAL_FIELDS} is
 * a reset, and those changes are the revert. A `KEEP` re-approve carries the
 * revival fields alone and yields nothing here, which is what `KEEP` means.
 */
export function revertedFields(revision: TaskRevision): Set<string> {
  if (revision.entryType !== 'reapproved') return new Set()
  return new Set(
    revision.changes.map((c) => c.field).filter((field) => !REVIVAL_FIELDS.has(field)),
  )
}

/**
 * Whether the trail shows this entry. A parked genesis is not shown (MYAC-1639):
 * it is the copy made when someone first commented on an undecided action, and
 * « X created the task » belongs to the acceptance that comes later.
 */
export function isShownRevision(revision: TaskRevision): boolean {
  return revision.entryType !== 'parked'
}

/** Whether this entry reverted content — i.e. was a `RESET` rather than a `KEEP`. */
export function isResetRevision(revision: TaskRevision): boolean {
  return revertedFields(revision).size > 0
}

/** A trail row: the entry, plus whether a later reset undid it. */
export interface TrailEntry {
  revision: TaskRevision
  /**
   * `seq` of the reset that superseded this entry, or `null`.
   *
   * Carried rather than a bare boolean so the view can group a run of collapsed
   * entries under the reset that swallowed them, instead of showing one
   * expander per row.
   */
  supersededBy: number | null
}

/**
 * The trail, with entries a later `RESET` undid marked as superseded.
 *
 * **Derived, never stored** (§5.4) — zero schema change, zero mutability, which
 * is the whole point: `RESET` appends and never deletes, so a cluttered trail
 * is a view problem and this is the view solving it.
 *
 * An entry is superseded when a later reset reverted a field it changed. Both
 * halves matter:
 *
 * - *A field it changed* — read off the reset's own `changes`, so nothing here
 *   mirrors `editable_fields`. A reset records only fields that actually
 *   differed, so an edit the user had already undone by hand is not marked
 *   superseded. That is correct: it was not reverted, it was cancelled out.
 * - *Later* — genesis is never superseded, because a reset restores **to** it,
 *   and an un-approve is not superseded either, since its `review_decision`
 *   change lies outside what a reset touches.
 *
 * Two resets are handled by taking the **first** one that supersedes an entry,
 * which is the one that actually undid it; a second reset re-reverts whatever
 * happened in between and claims only those.
 */
export function buildTrail(revisions: readonly TaskRevision[]): TrailEntry[] {
  const ordered = [...revisions].sort(bySeq)

  return ordered.map((revision) => {
    // **Inert against today's data, kept for a future writer.** Genesis carries
    // a `snapshot` and no `changes` (`apply_create`, and `heal` for a
    // backfilled one), so an empty `touched` already falls out of the search
    // below without this — the same shape as the guard dropped in 9056dc94, and
    // it is stated here rather than left to look load-bearing.
    //
    // Unlike that one it earns its place, because it encodes a rule rather than
    // short-circuiting a computation: a reset restores **to** genesis, so
    // genesis is not superseded whatever it comes to carry. A writer that ever
    // put `changes` on `seq 0` would otherwise see it collapse into the run it
    // is the baseline for.
    if (revision.seq === GENESIS_SEQ) {
      return { revision, supersededBy: null }
    }
    // An entry that changed no fields — an archive, say — can never intersect a
    // reset's reverted set, so it falls out of the search below without needing
    // a guard of its own.
    const touched = new Set(revision.changes.map((c) => c.field))
    const reset = ordered.find(
      (later) =>
        later.seq > revision.seq &&
        [...revertedFields(later)].some((field) => touched.has(field)),
    )
    return { revision, supersededBy: reset ? reset.seq : null }
  })
}
